import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeValue } from '@angular/platform-browser';
import { AttachmentType } from 'src/sb-tools/typescript/enums/attachment-type.enum';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DocumentViewerModalComponent } from '../document-viewer-modal/document-viewer-modal.component';
import { AttachmentBase } from 'src/sb-tools/typescript/models/attachment-base.model';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { DocumentWindowMessageType } from '@sb-document-viewer/enums/document-window-message-type';
import { GridGalleryComponent } from '@sb-document-viewer/components/grid-gallery/grid-gallery.component';
import { NgClass } from '@angular/common';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { FileViewImageComponent } from '@sb-document-viewer/components/file-view-image/file-view-image.component';
import { MultilineTruncateDirective } from '@sb-tools/typescript/directives/multiline-truncate.directive';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';

// todo: add logic to hide loader not by styles
@Component({
    selector: 'sb-file-view',
    templateUrl: './file-view.component.html',
    styleUrls: ['./file-view.component.scss'],
    imports: [
        GridGalleryComponent,
        CarouselModule,
        NgClass,
        FileViewImageComponent,
        MultilineTruncateDirective,
        NgxDocViewerModule,
        LoaderComponent,
    ],
})
export class FileViewComponent implements OnChanges, OnDestroy {
    @Input() attachment: AttachmentBase;
    @Input() attachments?: AttachmentBase[];
    @Input() isCarouselItem?: boolean = false;
    @Input() isEnablePostMessageForWidget?: boolean = false;
    @Input() isFilePreviewInMessage?: boolean = false;
    @Input() isFilePreviewInModal?: boolean = false;
    @Input() isFullWidthView?: boolean = false;
    @Input() isRoundedCorners?: boolean = true;
    @Input() permalink?: string;
    @Input() canBeShared?;
    @Input() reply?;
    @Input() isZoomAvailable?: boolean = false;

    @Output() forwardFile: EventEmitter<any> = new EventEmitter();

    attachmentTypeEnum = AttachmentType;
    fileUrl: string | SafeValue;
    googleDocURL: SafeResourceUrl;
    isGridGalleryCarousel: boolean;
    isIframeVideo: boolean;
    isIframeFacebookVideo: boolean;
    isLinksCarousel: boolean;
    isMSOfficeFile = false;
    isNgxDocViewerEnabled = false;
    itemsPerSlide = 1;

    constructor(
        private domSanitizer: DomSanitizer,
        private bsModalService: BsModalService,
    ) {}

    ngOnChanges() {
        if (
            this.attachment.attachmentType === this.attachmentTypeEnum.carousel &&
            this.attachment.attachments &&
            this.attachment.attachments.length
        ) {
            // @todo remove this logic when backend implements it
            this.attachment.isExpired = this.attachment.attachments.every((attachment) => {
                return attachment.isExpired;
            });

            this.isLinksCarousel = this.attachment.attachments.some((attachment) => {
                return attachment.attachmentType === this.attachmentTypeEnum.link;
            });

            this.isGridGalleryCarousel =
                this.attachment.mimeType !== 'application/instagram-carousel' &&
                this.attachment.attachments.every((attachment) => {
                    return attachment.attachmentType === this.attachmentTypeEnum.image;
                });

            if (this.isLinksCarousel) {
                this.itemsPerSlide = this.attachment.attachments.length > 2 ? 3 : this.attachment.attachments.length;
            } else {
                this.itemsPerSlide = 1;
            }
        }

        if (this.attachment.attachmentType === this.attachmentTypeEnum.iframe) {
            this.isIframeFacebookVideo =
                this.attachment.mimeType === 'iframe/facebook-video' &&
                this.attachment.url.indexOf('www.facebook.com/plugins/video') >= 0;

            this.isIframeVideo =
                [
                    'iframe/wistia-video',
                    'iframe/vimeo-video',
                    'iframe/youtube-video',
                    'iframe/tiktok-video',
                    'iframe/leadDesk-video',
                ].includes(this.attachment.mimeType) || this.isIframeFacebookVideo;

            this.fileUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
                this.domSanitizer.sanitize(SecurityContext.URL, this.attachment.url),
            );
        }

        this.isMSOfficeFile =
            this.attachment.attachmentType === this.attachmentTypeEnum.application &&
            HelperService.msOfficeFileTypesList.indexOf(
                HelperService.getFileType(HelperService.getFileExtension(this.attachment.name)),
            ) >= 0;

        this.isNgxDocViewerEnabled =
            [
                this.attachmentTypeEnum.image,
                this.attachmentTypeEnum.video,
                this.attachmentTypeEnum.iframe,
                this.attachmentTypeEnum.link,
                this.attachmentTypeEnum.carousel,
            ].indexOf(this.attachment.attachmentType) < 0;

        if (this.isNgxDocViewerEnabled) {
            if (!this.isMSOfficeFile) {
                this.googleDocURL = this.domSanitizer.bypassSecurityTrustResourceUrl(
                    'https://docs.google.com/gview?url=' + this.attachment.url + '&embedded=true',
                );
                this.checkAndReloadGoogleDoc();
            }
        }
    }

    openDocumentViewerModal(event) {
        if (this.isEnablePostMessageForWidget) {
            window.parent.postMessage(
                {
                    messageType: DocumentWindowMessageType.openPreviewModal,
                    attachment: this.attachment,
                },
                '*',
            );
            return;
        }

        const initialState = {
            event,
            isImage: this.attachment.attachmentType === AttachmentType.image,
            attachments: [this.attachment],
            fileIndex: 0,
            isFilePreviewInMessage: true,
            permalink: this.permalink,
            reply: this.reply,
            isForwardAllowed: this.canBeShared,
            onForwardFile: (file) => {
                return this.forwardFile.emit(file);
            },
        };

        this.bsModalService.show(DocumentViewerModalComponent, {
            initialState,
            class: 'modal-lg modal-document-viewer',
        });
    }

    loadErrorImage(event: Event): void {
        (event.target as HTMLImageElement).src = './sb-tools/theme/images/broken-attachment.png';
    }

    processLoadedIframe() {
        const iframe = document.getElementById('iframe-doc-viewer') as HTMLIFrameElement;
        if (iframe.contentDocument === null) {
            document.body.classList.add('is-doc-iframe-loaded');
        }
    }

    private checkAndReloadGoogleDoc(): void {
        const timerId = setInterval(() => {
            const docViewerIframe = document.getElementById('iframe-doc-viewer') as HTMLIFrameElement;
            if (document.body.classList.contains('is-doc-iframe-loaded') || !docViewerIframe) {
                clearInterval(timerId);
            } else {
                docViewerIframe.src = docViewerIframe.src || '';
            }
        }, 3000);
    }

    ngOnDestroy() {
        document.body.classList.remove('is-doc-iframe-loaded');
    }
}
